import { Avatar, Box, Checkbox, Chip, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { PackingListItem } from '../schema';
import { useState } from 'react';
import { db } from '../instant';

interface Props {
    packingListItem: PackingListItem;
    eventId: string;
    eventName?: string;
    allItemLocations: Array<{
        id: string;
        quantity: number;
        itemType?: { id: string } | null;
        event?: { id: string } | null;
    }>;
    isReadOnly?: boolean;
}

type PackingStatus = 'packed' | 'partial' | 'notPacked';

interface StatusConfig {
    icon: React.ReactNode;
    color: string;
    message: string;
    bgColor: string;
}

export default function ConfirmationChecklistRow({ packingListItem, eventId, eventName, allItemLocations, isReadOnly = false }: Props) {
    const itemType = packingListItem.itemType;
    const category = itemType?.category;
    const requiredQty = packingListItem.quantity || 0;
    const confirmed = packingListItem.confirmed || false;
    const [isUpdating, setIsUpdating] = useState(false);

    // Find current quantity at target event
    const currentQuantity = (() => {
        if (!itemType) return 0;
        for (const location of allItemLocations) {
            if (location.itemType?.id === itemType.id && location.event?.id === eventId) {
                return location.quantity || 0;
            }
        }
        return 0;
    })();

    // Determine packing status
    const status: PackingStatus = (() => {
        if (currentQuantity >= requiredQty) {
            return 'packed';
        } else if (currentQuantity > 0) {
            return 'partial';
        } else {
            return 'notPacked';
        }
    })();

    // Get location information
    const locationInfo = (() => {
        if (!itemType) return 'Unknown location';

        // Find all locations for this item type
        const locations = allItemLocations.filter(loc => loc.itemType?.id === itemType.id && (loc.quantity || 0) > 0);

        if (locations.length === 0) {
            return 'Not packed';
        }

        // Check if all items are at target event
        const atTargetEvent = locations.filter(loc => loc.event?.id === eventId);
        const totalAtTarget = atTargetEvent.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

        if (atTargetEvent.length > 0 && totalAtTarget >= requiredQty) {
            // All required items are at target event
            // Since items aren't tracked per-trailer, we show "At [Event Name]"
            // For trailers, we could show "In Trailer X" but items aren't tracked per-trailer
            return eventName ? `At ${eventName}` : 'At event';
        }

        // Mixed locations or at HQ
        const atHQ = locations.filter(loc => !loc.event);
        const atOtherEvents = locations.filter(loc => loc.event && loc.event.id !== eventId);
        const totalAtHQ = atHQ.reduce((sum, loc) => sum + (loc.quantity || 0), 0);
        const totalAtOther = atOtherEvents.reduce((sum, loc) => sum + (loc.quantity || 0), 0);

        if (totalAtHQ > 0 && totalAtOther > 0) {
            return 'Mixed locations';
        } else if (totalAtHQ > 0) {
            return 'At HQ';
        } else if (totalAtOther > 0) {
            return 'At other events';
        }

        return 'Unknown location';
    })();

    // Status configuration
    const statusConfig: Record<PackingStatus, StatusConfig> = {
        packed: {
            icon: <CheckCircleIcon sx={{ color: 'success.main' }} />,
            color: 'success.main',
            message: `Packed (${currentQuantity} items)`,
            bgColor: 'success.light',
        },
        partial: {
            icon: <WarningIcon sx={{ color: 'warning.main' }} />,
            color: 'warning.main',
            message: `Only ${currentQuantity} of ${requiredQty} items`,
            bgColor: 'warning.light',
        },
        notPacked: {
            icon: <ErrorIcon sx={{ color: 'error.main' }} />,
            color: 'error.main',
            message: 'Not packed',
            bgColor: 'error.light',
        },
    };

    const config = statusConfig[status];
    const categoryColor = category?.color || '#2196F3';

    const handleConfirmationChange = async (checked: boolean) => {
        setIsUpdating(true);
        try {
            await db.transact([
                db.tx.packingListItems[packingListItem.id].update({
                    confirmed: checked,
                }),
            ]);
        } catch (error) {
            console.error('Failed to update confirmation:', error);
            // Revert checkbox on error - the query will handle this automatically
        } finally {
            setIsUpdating(false);
        }
    };

    // Row background color based on status and confirmation
    const rowBgColor = confirmed
        ? 'action.hover' // Dimmed when confirmed
        : config.bgColor
          ? `${config.bgColor}20` // Light background based on status
          : 'transparent';

    return (
        <TableRow
            sx={{
                bgcolor: rowBgColor,
                '&:hover': {
                    bgcolor: confirmed ? 'action.selected' : `${config.bgColor}30`,
                },
            }}
        >
            {/* Confirmation Checkbox */}
            <TableCell padding="checkbox">
                <Checkbox
                    checked={confirmed}
                    onChange={e => handleConfirmationChange(e.target.checked)}
                    disabled={isUpdating || isReadOnly}
                    title={isReadOnly ? 'Operation is completed (read-only)' : undefined}
                />
            </TableCell>

            {/* Photo */}
            <TableCell>
                <Avatar
                    src={itemType?.photoUrl || undefined}
                    variant="rounded"
                    sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'grey.200',
                    }}
                >
                    {!itemType?.photoUrl && <LocalOfferIcon />}
                </Avatar>
            </TableCell>

            {/* Name + Category */}
            <TableCell>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                        {itemType?.name || 'Unknown Item'}
                    </Typography>
                    {category && (
                        <Chip
                            label={category.name}
                            size="small"
                            sx={{
                                bgcolor: categoryColor,
                                color: 'white',
                                fontSize: '0.7rem',
                                width: 'fit-content',
                            }}
                        />
                    )}
                </Box>
            </TableCell>

            {/* Required Quantity */}
            <TableCell>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                    {requiredQty}
                </Typography>
            </TableCell>

            {/* Status Indicator */}
            <TableCell>
                <Tooltip title={config.message}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {config.icon}
                        <Typography variant="body2" sx={{ color: config.color }}>
                            {status === 'packed' && 'Packed'}
                            {status === 'partial' && 'Partial'}
                            {status === 'notPacked' && 'Not Packed'}
                        </Typography>
                    </Box>
                </Tooltip>
            </TableCell>

            {/* Current Location */}
            <TableCell>
                <Typography variant="body2" color="text.secondary">
                    {locationInfo}
                </Typography>
            </TableCell>
        </TableRow>
    );
}
